/* eslint-disable i18next/no-literal-string -- Document metadata is static deployment configuration, not runtime UI copy. */
import '../init';

import { AnalyticsProvider } from '@rhim/analytics';
import config from '@rhim/config';
import { settings } from '@rhim/design';
import { i18nReact } from '@rhim/i18n';
import { ErrorBoundary as RhimErrorBoundary, Frame, NoPrint, NoPrintAppWrapper } from '@rhim/react';
import { useConnectionListener, useCookiebot } from '@rhim/react/hooks';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { type ReactNode, useCallback, useEffect } from 'react';
import { Links, Meta, Outlet, Scripts, ScrollRestoration } from 'react-router';

import { environment } from '../environments/environment';
import { mixpanelAnalytics } from '../lib/analytics';
import { HiddenNavigationProvider } from '../pages/OperatorDisplay/useHiddenNavigation';
import { useLogger } from '../utilities';
import { queryClient } from '../utilities/queryClient';

const { GlobalStyle } = settings.globals;
const COOKIEBOT_CBID = 'bb03b04f-2940-427a-94e1-06fdde42049a';

export { StaticPageLoadingScreen as HydrateFallback } from '@rhim/react';

interface LayoutProps {
  readonly children: ReactNode;
}

export function Layout({ children }: Readonly<LayoutProps>) {
  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <title>HMD | Heat Management Desk</title>
        <base href={import.meta.env.BASE_URL} />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta httpEquiv="Cache-Control" content="no-cache, no-store, must-revalidate" />
        <meta httpEquiv="Pragma" content="no-cache" />
        <link rel="apple-touch-icon" href="apple-touch-icon.png" sizes="180x180" />
        <link rel="shortcut icon" type="image/x-icon" href="favicon.ico" />
        <link rel="icon" type="image/svg+xml" href="assets/fav-icons/favicon.svg" />
        <link rel="icon" type="image/png" href="assets/fav-icons/favicon-96x96.png" sizes="96x96" />
        <link rel="manifest" href="site.webmanifest" />
        <link type="text/css" rel="stylesheet" href="assets/Fonts/v2/NortW05.css" />
        <link type="text/css" rel="stylesheet" href="assets/cookiebot/styles.css" />
        <Meta />
        <Links />
      </head>
      <body>
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default function App() {
  return (
    <>
      <GlobalStyle />
      <HiddenNavigationProvider initialValue={false}>
        <QueryClientProvider client={queryClient}>
          <NoPrintAppWrapper>
            <NoPrint>{environment.allowExtensions && <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-right" />}</NoPrint>
            <Frame>
              <RhimErrorBoundary>
                <AnalyticsProvider analytics={mixpanelAnalytics}>
                  <AppRuntime />
                  <Outlet />
                </AnalyticsProvider>
              </RhimErrorBoundary>
            </Frame>
          </NoPrintAppWrapper>
        </QueryClientProvider>
      </HiddenNavigationProvider>
    </>
  );
}

function AppRuntime() {
  useConnectionListener(typeof window !== 'undefined' && window.self === window.top);
  useLogger();
  useCookiebot(COOKIEBOT_CBID);

  const handleKeyboardShortcut = useCallback((event: KeyboardEvent) => {
    if (event.ctrlKey && config.version === 'dev' && event.key === 'Z') {
      void i18nReact.toggleCimode();
    }
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyboardShortcut);
    return () => document.removeEventListener('keydown', handleKeyboardShortcut);
  }, [handleKeyboardShortcut]);

  return null;
}
